import { FC } from 'react';
import { Box, Button, Typography } from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { useTranslation } from 'react-i18next';
import { useIsDemoUser } from 'src/components/hooks/useIsDemoUser';
import { useDemoFirstUploadTrim } from 'src/components/hooks/useDemoFirstUploadTrim';
import { BOOK_DEMO_URL, pricingPlansUrl } from 'src/utils/siteLinks';
import { sendEditorMetric } from 'src/utils/editorMetrics';
import { useDemoSource } from 'src/components/hooks/useDemoSource';

export const DemoAccountBanner: FC = () => {
  const { t, i18n } = useTranslation();
  const isDemoUser = useIsDemoUser();
  const trim = useDemoFirstUploadTrim();
  const source = useDemoSource();

  return (
    <Box
      sx={{
        px: { xs: 1.25, sm: 2, md: 4 },
        py: { xs: 0.75, sm: 1.25 },
        background: ({ palette }) =>
          `linear-gradient(90deg, ${palette.primary.main}, ${palette.primary.dark})`,
        color: 'primary.contrastText',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: { xs: 1, sm: 1.5 },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          fontWeight={700}
          noWrap
          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
        >
          {t('demoAccountBannerTitle')}
        </Typography>
        <Typography
          variant="body2"
          sx={{ display: { xs: 'none', sm: 'block' } }}
        >
          {t('demoAccountBannerOffer')}
        </Typography>
        {trim && (
          <Typography
            variant="body2"
            role="status"
            data-testid="demo-page-limit-notice"
            sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          >
            {t('demoPageLimitNotice', {
              used: trim.usedPages,
              total: trim.totalPages,
            })}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <Button
          component="a"
          href={BOOK_DEMO_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            if (isDemoUser) {
              sendEditorMetric('editor_banner_click', { target: 'booking', source }, false);
            }
          }}
          variant="outlined"
          color="inherit"
          aria-label={t('bookDemo') as string}
          sx={{
            flexShrink: 0,
            fontWeight: 700,
            minWidth: { xs: 'auto', sm: 64 },
            px: { xs: 1, sm: 2 },
            py: { xs: 0.375, sm: 0.75 },
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            borderColor: 'primary.contrastText',
          }}
        >
          {/* Phones show the icon only, so the title keeps its space. */}
          <EventAvailableIcon
            fontSize="small"
            sx={{ display: { xs: 'block', sm: 'none' } }}
          />
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            {t('bookDemo')}
          </Box>
        </Button>
        <Button
          component="a"
          href={pricingPlansUrl(i18n.language)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            if (isDemoUser) {
              sendEditorMetric('editor_banner_click', { target: 'pricing', source }, false);
            }
          }}
          variant="contained"
          color="inherit"
          aria-label={t('viewPricing') as string}
          sx={{
            color: 'primary.main',
            flexShrink: 0,
            fontWeight: 700,
            minWidth: { xs: 'auto', sm: 64 },
            px: { xs: 1, sm: 2 },
            py: { xs: 0.375, sm: 0.75 },
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
          }}
        >
          {t('viewPricing')}
        </Button>
      </Box>
    </Box>
  );
};
